/**
 * Shopify Collections API
 * Functions for fetching collection data using the Shopify Storefront API
 */

import { storefrontClient } from "../client";

/**
 * Fetch a list of collections from Shopify
 * @param limit Number of collections to fetch (default: 10)
 * @returns Promise with collection data
 */
export const fetchShopifyCollections = async (limit = 10) => {
  try {
    const { data } = await storefrontClient.request(
      `
        query GetCollections($limit: Int!) {
          collections(first: $limit) {
            edges {
              node {
                id
                title
                handle
                description
                descriptionHtml
                image {
                  url
                  altText
                  width
                  height
                }
                products(first: 10) {
                  edges {
                    node {
                      id
                      title
                      handle
                      description
                      priceRange {
                        minVariantPrice {
                          amount
                          currencyCode
                        }
                        maxVariantPrice {
                          amount
                          currencyCode
                        }
                      }
                      images(first: 1) {
                        edges {
                          node {
                            url
                            altText
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `,
      {
        variables: {
          limit: limit,
        },
      },
    );

    return data.collections;
  } catch (error) {
    console.error("Error fetching collections from Shopify:", error);
    throw error;
  }
};

/**
 * Fetch a single collection by handle from Shopify
 * @param handle The collection handle (slug)
 * @param productLimit Number of products to fetch in the collection (default: 20)
 * @returns Promise with collection data
 */
export const fetchShopifyCollectionByHandle = async (
  handle: string,
  productLimit = 20,
  sortKey?: string,
  reverse?: boolean,
  priceMin?: number,
  priceMax?: number,
  available?: boolean,
) => {
  // Decode the URL-encoded handle
  const decodedHandle = decodeURIComponent(handle);
  try {
    const { data } = await storefrontClient.request(
      `
        query GetCollectionByHandle(
          $handle: String!,
          $productLimit: Int!,
          $sortKey: ProductCollectionSortKeys,
          $reverse: Boolean,
          $filters: [ProductFilter!]
        ) {
          collection(handle: $handle) {
            id
            title
            handle
            description
            descriptionHtml
            image {
              url
              altText
              width
              height
            }
            seo {
              title
              description
            }
            metafields(
              identifiers: [
                { namespace: "custom", key: "mobile_image" }
                { namespace: "custom", key: "desktop_image" }
                { namespace: "custom", key: "footer_content" }
                {namespace: "translations", key: "title_hi"},
                {namespace: "translations", key: "description_hi"},
                {namespace: "translations", key: "short_description_hi"}
              ]
            ) {
              key
              namespace
              type
              value
              reference {
                ... on MediaImage {
                  image {
                    url
                    altText
                  }
                }
              }
            }
            products(
              first: $productLimit,
              sortKey: $sortKey,
              reverse: $reverse,
              filters: $filters
            ) {
              edges {
                node {
                  id
                  title
                  handle
                  description
                  availableForSale
                  metafields(
                    identifiers: [
                      { namespace: "reviews", key: "rating" }
                      {namespace: "translations", key: "title_hi"},
                      {namespace: "translations", key: "description_hi"},
                      {namespace: "translations", key: "short_description_hi"}
                    ]
                  ) {
                    key
                    namespace
                    value
                    type
                  }
                  priceRange {
                    minVariantPrice {
                      amount
                      currencyCode
                    }
                    maxVariantPrice {
                      amount
                      currencyCode
                    }
                  }
                  images(first: 1) {
                    edges {
                      node {
                        url
                        altText
                      }
                    }
                  }
                  variants(first: 5) {
                    edges {
                      node {
                        id
                        title
                        price {
                          amount
                          currencyCode
                        }
                        compareAtPrice {
                          amount
                          currencyCode
                        }
                        availableForSale
                        sku
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `,
      {
        variables: {
          handle: decodedHandle,
          productLimit: productLimit,
          sortKey: getSortKey(sortKey),
          reverse: reverse || false,
          filters: buildFilters(priceMin, priceMax, available),
        },
      },
    );

    return data.collection;
  } catch (error) {
    console.error(
      `Error fetching collection with handle ${handle} from Shopify:`,
      error,
    );
    throw error;
  }
};

/**
 * Convert sort option to Shopify sort key
 */
const getSortKey = (sortKey?: string): string => {
  // Log the incoming sort key
  console.log("getSortKey received:", sortKey);

  // Return the sort key directly since it's already in the correct format
  return sortKey || "MANUAL";
};

/**
 * Build product filters based on parameters
 */
interface ProductFilter {
  price?: {
    min: number;
    max: number;
  };
  available?: boolean;
}

const buildFilters = (
  priceMin?: number,
  priceMax?: number,
  available?: boolean,
) => {
  const filters: ProductFilter[] = [];

  if (priceMin !== undefined || priceMax !== undefined) {
    filters.push({
      price: {
        min: Number.parseFloat((priceMin || 0).toFixed(2)),
        max: Number.parseFloat((priceMax || 1000000).toFixed(2)),
      },
    });
  }

  if (available !== undefined) {
    filters.push({
      available: available,
    });
  }

  return filters;
};
